import React from 'react';
import { P, match } from 'ts-pattern';
import type * as TypeFest from 'type-fest';
import TablerIconFileCode from '~icons/tabler/file-code';
import TablerIconFileTypeCss from '~icons/tabler/file-type-css';
import TablerIconFileTypeCsv from '~icons/tabler/file-type-csv';
import TablerIconFileTypeDoc from '~icons/tabler/file-type-doc';
import TablerIconFileTypeHtml from '~icons/tabler/file-type-html';
import TablerIconFileTypeJpg from '~icons/tabler/file-type-jpg';
import TablerIconFileTypeJs from '~icons/tabler/file-type-js';
import TablerIconFileTypeJsx from '~icons/tabler/file-type-jsx';
import TablerIconFileTypePdf from '~icons/tabler/file-type-pdf';
import TablerIconFileTypePhp from '~icons/tabler/file-type-php';
import TablerIconFileTypePng from '~icons/tabler/file-type-png';
import TablerIconFileTypeRs from '~icons/tabler/file-type-rs';
import TablerIconFileTypeSql from '~icons/tabler/file-type-sql';
import TablerIconFileTypeSvg from '~icons/tabler/file-type-svg';
import TablerIconFileTypeTs from '~icons/tabler/file-type-ts';
import TablerIconFileTypeTsx from '~icons/tabler/file-type-tsx';
import TablerIconFileTypeTxt from '~icons/tabler/file-type-txt';
import TablerIconFileTypeVue from '~icons/tabler/file-type-vue';
import TablerIconFileTypeXml from '~icons/tabler/file-type-xml';
import TablerIconFileTypeZip from '~icons/tabler/file-type-zip';
import VscodeIconsDefaultFile from '~icons/vscode-icons/default-file';
import VscodeIconsFileTypeC from '~icons/vscode-icons/file-type-c';
import VscodeIconsFileTypeCoffee from '~icons/vscode-icons/file-type-coffeescript';
import VscodeIconsFileTypeCpp from '~icons/vscode-icons/file-type-cpp';
import VscodeIconsFileTypeCsharp from '~icons/vscode-icons/file-type-csharp';
import VscodeIconsFileTypeCss from '~icons/vscode-icons/file-type-css';
import VscodeIconsFileTypeDocker from '~icons/vscode-icons/file-type-docker';
import VscodeIconsFileTypeEditorconfig from '~icons/vscode-icons/file-type-editorconfig';
import VscodeIconsFileTypeGit from '~icons/vscode-icons/file-type-git';
import VscodeIconsFileTypeGo from '~icons/vscode-icons/file-type-go';
import VscodeIconsFileTypeGraphql from '~icons/vscode-icons/file-type-graphql';
import VscodeIconsFileTypeHtml from '~icons/vscode-icons/file-type-html';
import VscodeIconsFileTypeImage from '~icons/vscode-icons/file-type-image';
import VscodeIconsFileTypeJava from '~icons/vscode-icons/file-type-java';
import VscodeIconsFileTypeJson from '~icons/vscode-icons/file-type-json';
import VscodeIconsFileTypeKotlin from '~icons/vscode-icons/file-type-kotlin';
import VscodeIconsFileTypeLess from '~icons/vscode-icons/file-type-less';
import VscodeIconsFileTypeMarkdown from '~icons/vscode-icons/file-type-markdown';
import VscodeIconsFileTypeMermaid from '~icons/vscode-icons/file-type-mermaid';
import VscodeIconsFileTypeObjectiveC from '~icons/vscode-icons/file-type-objectivec';
import VscodeIconsFileTypeObjectiveCpp from '~icons/vscode-icons/file-type-objectivecpp';
import VscodeIconsFileTypePhp from '~icons/vscode-icons/file-type-php';
import VscodeIconsFileTypePrisma from '~icons/vscode-icons/file-type-prisma';
import VscodeIconsFileTypePython from '~icons/vscode-icons/file-type-python';
import VscodeIconsFileTypeRuby from '~icons/vscode-icons/file-type-ruby';
import VscodeIconsFileTypeRust from '~icons/vscode-icons/file-type-rust';
import VscodeIconsFileTypeSass from '~icons/vscode-icons/file-type-sass';
import VscodeIconsFileTypeScss from '~icons/vscode-icons/file-type-scss';
import VscodeIconsFileTypeShell from '~icons/vscode-icons/file-type-shell';
import VscodeIconsFileTypeSql from '~icons/vscode-icons/file-type-sql';
import VscodeIconsFileTypeStylus from '~icons/vscode-icons/file-type-stylus';
import VscodeIconsFileTypeSvg from '~icons/vscode-icons/file-type-svg';
import VscodeIconsFileTypeSwift from '~icons/vscode-icons/file-type-swift';
import VscodeIconsFileTypeTerraform from '~icons/vscode-icons/file-type-terraform';
import VscodeIconsFileTypeTypescriptOfficial from '~icons/vscode-icons/file-type-typescript-official';
import VscodeIconsFileTypeVue from '~icons/vscode-icons/file-type-vue';
import VscodeIconsFileTypeXml from '~icons/vscode-icons/file-type-xml';
import VscodeIconsFileTypeYaml from '~icons/vscode-icons/file-type-yaml';
import { type CodeMirrorLang, useCodeMirrorLangs } from '#/lib/codemirror';

export type GitHubFileIconLanguage = TypeFest.Simplify<CodeMirrorLang | 'csv'>;

export interface GitHubFileIconProps
  extends React.ComponentProps<typeof TablerIconFileCode> {
  fileExtension?: string;
  /**
   * The type of icon to display.
   * @default 'file'
   */
  iconType?: 'file' | 'brand';
}

/**
 * A component that displays a file icon based on the file extension.
 */
export const GitHubFileIcon = React.forwardRef<
  SVGSVGElement,
  GitHubFileIconProps
>(({ fileExtension = '', iconType = 'brand', ...props }, ref) => {
  const supportedLanguages = useCodeMirrorLangs();

  const Icon = React.useMemo(() => {
    const cleanFileExtension = fileExtension.replace('.', '');

    // Support Core Languages
    const language = supportedLanguages.findByFileExtension(fileExtension);

    if (iconType === 'brand') {
      return getBrandIcon(
        language === 'plaintext' ? cleanFileExtension : language
      );
    }

    if (
      ['csv', 'zip', 'png', 'jpg', 'svg', 'pdf'].includes(cleanFileExtension)
    ) {
      return getSpecialIcon(cleanFileExtension);
    }

    return getFileIcon(language);
  }, [fileExtension, iconType, supportedLanguages]);

  return <Icon ref={ref} {...props} />;
});
GitHubFileIcon.displayName = 'GitHubFileIcon';

/*
|------------------
| Internal
|------------------
*/

function getBrandIcon(
  language: TypeFest.LiteralUnion<GitHubFileIconLanguage, string>
) {
  return match(language)
    .when(
      (extension) => extension.includes('git'),
      () => VscodeIconsFileTypeGit
    )
    .with('editorconfig', () => VscodeIconsFileTypeEditorconfig)
    .with('typescript', () => VscodeIconsFileTypeTypescriptOfficial)
    .with('python', () => VscodeIconsFileTypePython)
    .with('java', () => VscodeIconsFileTypeJava)
    .with('cpp', () => VscodeIconsFileTypeCpp)
    .with('csharp', () => VscodeIconsFileTypeCsharp)
    .with('go', () => VscodeIconsFileTypeGo)
    .with('svg', () => VscodeIconsFileTypeSvg)
    .with(
      P.union('png', 'jpg', 'jpeg', 'gif', 'webp'),
      () => VscodeIconsFileTypeImage
    )
    .with('rust', () => VscodeIconsFileTypeRust)
    .with('ruby', () => VscodeIconsFileTypeRuby)
    .with('php', () => VscodeIconsFileTypePhp)
    .with('swift', () => VscodeIconsFileTypeSwift)
    .with('kotlin', () => VscodeIconsFileTypeKotlin)
    .with('objectiveC', () => VscodeIconsFileTypeObjectiveC)
    .with('javascript', () => VscodeIconsFileTypeTypescriptOfficial)
    .with('jsx', () => VscodeIconsFileTypeTypescriptOfficial)
    .with('tsx', () => VscodeIconsFileTypeTypescriptOfficial)
    .with('html', () => VscodeIconsFileTypeHtml)
    .with('css', () => VscodeIconsFileTypeCss)
    .with('markdown', () => VscodeIconsFileTypeMarkdown)
    .with('xml', () => VscodeIconsFileTypeXml)
    .with('sql', () => VscodeIconsFileTypeSql)
    .with('vue', () => VscodeIconsFileTypeVue)
    .with('plaintext', () => VscodeIconsDefaultFile)
    .with('shell', () => VscodeIconsFileTypeShell)
    .with('c', () => VscodeIconsFileTypeC)
    .with('coffeescript', () => VscodeIconsFileTypeCoffee)
    .with('dockerfile', () => VscodeIconsFileTypeDocker)
    .with('graphql', () => VscodeIconsFileTypeGraphql)
    .with('hcl', () => VscodeIconsFileTypeTerraform)
    .with('jsp', () => VscodeIconsFileTypeJava)
    .with('json', () => VscodeIconsFileTypeJson)
    .with('less', () => VscodeIconsFileTypeLess)
    .with('mermaid', () => VscodeIconsFileTypeMermaid)
    .with('objectiveCpp', () => VscodeIconsFileTypeObjectiveCpp)
    .with('prisma', () => VscodeIconsFileTypePrisma)
    .with('sass', () => VscodeIconsFileTypeSass)
    .with('scss', () => VscodeIconsFileTypeScss)
    .with('stylus', () => VscodeIconsFileTypeStylus)
    .with('yaml', () => VscodeIconsFileTypeYaml)
    .otherwise(() => VscodeIconsDefaultFile);
}

function getFileIcon(language: GitHubFileIconLanguage) {
  return match(language)
    .with('css', () => TablerIconFileTypeCss)
    .with('rust', () => TablerIconFileTypeRs)
    .with('typescript', () => TablerIconFileTypeTs)
    .with('tsx', () => TablerIconFileTypeTsx)
    .with('javascript', () => TablerIconFileTypeJs)
    .with('jsx', () => TablerIconFileTypeJsx)
    .with('html', () => TablerIconFileTypeHtml)
    .with('xml', () => TablerIconFileTypeXml)
    .with('sql', () => TablerIconFileTypeSql)
    .with('php', () => TablerIconFileTypePhp)
    .with('vue', () => TablerIconFileTypeVue)
    .with('markdown', () => TablerIconFileTypeDoc)
    .with('plaintext', () => TablerIconFileTypeTxt)
    .otherwise(() => TablerIconFileCode);
}

function getSpecialIcon(fileExtension: string) {
  return match(fileExtension)
    .with('csv', () => TablerIconFileTypeCsv)
    .with('zip', () => TablerIconFileTypeZip)
    .with('png', () => TablerIconFileTypePng)
    .with('jpg', () => TablerIconFileTypeJpg)
    .with('svg', () => TablerIconFileTypeSvg)
    .with('pdf', () => TablerIconFileTypePdf)
    .otherwise(() => TablerIconFileCode);
}
